import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Clock,
  MapPin,
  User,
  Phone,
  Mail,
  CreditCard,
  Banknote,
  Utensils,
  ShoppingBag,
  Package,
  Building2,
  Printer,
  PackageCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { Order, OrderStatus, OrderItem } from "@/types/orders";
import { COMPANY_FISCAL_DATA, LOCATION_FISCAL_DATA, IGIC_RATE, VERIFACTU_TEXT } from "@/constants/fiscal";

// Extended order type with joined location data and items with customizations
interface OrderItemWithCustomization {
  id: string;
  order_id: string;
  product_name: string;
  product_category?: string;
  unit_price: number;
  quantity: number;
  subtotal: number;
  created_at: string;
  removed_ingredients?: string[] | null;
}

interface OrderWithLocation extends Order {
  locations?: { id: string; code: string; name: string } | null;
  items?: OrderItemWithCustomization[];
  cash_received?: number;
  change_returned?: number;
  warehouse_status?: string;
}

interface OrderCardProps {
  order: OrderWithLocation;
  onStatusChange: (orderId: string, status: OrderStatus) => void;
  onMarkAsPaid?: (orderId: string) => void;
  onPrintTicket?: (order: OrderWithLocation) => void;
  onPrintCuenta?: (order: OrderWithLocation) => void;
  showLocation?: boolean;
  isEncargoMode?: boolean;
}

const OrderCard = ({ order, onStatusChange, onMarkAsPaid, onPrintTicket, onPrintCuenta, showLocation = false, isEncargoMode = false }: OrderCardProps) => {
  // Card color based on status AND payment
  // REGLA: pago_en_tienda = NO pagado todavía. SOLO 'pagado' es verde.
  const getCardColor = () => {
    if (order.status === "completado") {
      // SOLO verde si está realmente PAGADO
      if (order.payment_status === "pagado") {
        return "bg-green-50 border-green-300";
      }
      // Cualquier otro payment_status (incluyendo pago_en_tienda) = Naranja
      return "bg-orange-50 border-orange-300";
    }
    // En Almacén = Purple
    if (order.status === "en_almacen") {
      return "bg-purple-50 border-purple-300";
    }
    // Other statuses
    switch (order.status) {
      case "pendiente":
        return "bg-red-100 border-red-300";
      case "preparando":
        return "bg-yellow-100 border-yellow-300";
      case "cancelado":
        return "bg-gray-100 border-gray-300";
      default:
        return "";
    }
  };

  // Badge color for status display
  // REGLA: pago_en_tienda = NO pagado. SOLO 'pagado' es verde.
  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case "pendiente":
        return "bg-red-100 text-red-700 border-red-300";
      case "preparando":
        return "bg-yellow-100 text-yellow-700 border-yellow-300";
      case "en_almacen":
        return "bg-purple-100 text-purple-700 border-purple-300";
      case "completado":
        // SOLO verde si está realmente PAGADO
        if (order.payment_status === "pagado") {
          return "bg-green-100 text-green-700 border-green-300";
        }
        return "bg-orange-100 text-orange-700 border-orange-300";
      case "cancelado":
        return "bg-gray-100 text-gray-700 border-gray-300";
      default:
        return "";
    }
  };

  // REGLA: pago_en_tienda = NO pagado. SOLO 'pagado' muestra 🟢.
  const getStatusEmoji = () => {
    if (order.status === "completado") {
      // SOLO verde si está realmente PAGADO
      return order.payment_status === "pagado" ? "✅" : "🟠";
    }
    switch (order.status) {
      case "pendiente":
        return "🔴";
      case "preparando":
        return "🟡";
      case "en_almacen":
        return "📦";
      case "cancelado":
        return "⚫";
      default:
        return "";
    }
  };

  // Get status label for display
  const getStatusLabel = () => {
    switch (order.status) {
      case "pendiente":
        return "Pendiente";
      case "preparando":
        return "Preparando";
      case "en_almacen":
        return "En Almacén";
      case "completado":
        return order.payment_status === "pagado" ? "Completado" : "Sin Pagar";
      case "cancelado":
        return "Cancelado";
      default:
        return order.status;
    }
  };

  const getOrderTypeIcon = () => {
    switch (order.order_type) {
      case "mesa":
        return <Utensils className="h-4 w-4" />;
      case "llevar":
        return <ShoppingBag className="h-4 w-4" />;
      case "encargo":
        return <Package className="h-4 w-4" />;
    }
  };

  const getOrderTypeLabel = () => {
    switch (order.order_type) {
      case "mesa":
        return `Mesa ${order.table_number}`;
      case "llevar":
        return "Recoger";
      case "encargo":
        return "Encargo";
    }
  };

  const getOrderTypeBadgeStyle = () => {
    switch (order.order_type) {
      case "mesa":
        return "bg-green-100 text-green-700 border-green-300";
      case "llevar":
        return "bg-blue-100 text-blue-700 border-blue-300";
      case "encargo":
        return "bg-orange-100 text-orange-700 border-orange-300";
    }
  };

  const formatTime = (dateStr: string) => {
    return format(new Date(dateStr), "HH:mm", { locale: es });
  };

  // --- MODIFICADO: Salto directo a Completado ---
  const nextStatus = (): OrderStatus | null => {
    switch (order.status) {
      case "pendiente":
        return "completado"; // Salto directo
      case "preparando":
        return "completado"; // Por si acaso queda alguno viejo
      default:
        return null;
    }
  };

  const nextStatusLabel = () => {
    switch (order.status) {
      case "pendiente":
        return "Marcar como Listo"; // Texto directo
      case "preparando":
        return "Marcar como Listo";
      default:
        return null;
    }
  };
  // ----------------------------------------------

  const escapeHtml = (text: string | null | undefined): string => {
    if (!text) return "";
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  };

  const handlePrint = () => {
    // Use global print handler if provided (preferred - avoids blank ticket issues)
    if (onPrintTicket) {
      onPrintTicket(order);
      return;
    }
    
    // Fallback to window.open (legacy)
    const printWindow = window.open("", "_blank", "width=302,height=800");
    if (!printWindow) return;

    const formatDate = (dateStr: string) => {
      return format(new Date(dateStr), "dd/MM/yy", { locale: es });
    };

    const formatTime = (dateStr: string) => {
      return format(new Date(dateStr), "HH:mm", { locale: es });
    };

    const formatDateTime = (dateStr: string) => {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm:ss", { locale: es });
    };

    // Get location data - default to siete-palmas
    const locationCode = order.locations?.code || "siete-palmas";
    const locationFiscal = LOCATION_FISCAL_DATA[locationCode] || LOCATION_FISCAL_DATA["siete-palmas"];

    const year = new Date(order.created_at).getFullYear();
    const invoiceNumber = `${locationFiscal.serieFactura}-${year}-${order.order_number.toString().padStart(8, "0")}`;

    // Tax calculations
    const totalWithTax = order.total;
    const baseImponible = totalWithTax / (1 + IGIC_RATE);
    const cuotaIGIC = totalWithTax - baseImponible;

    // Loyalty mock data
    const pointsEarned = Math.floor(order.total);
    const loyaltyMemberNumber = order.customer_phone 
      ? "****" + order.customer_phone.slice(-4) 
      : "****0000";
    const totalPoints = pointsEarned + 113; // Mock accumulated

    // Items HTML with mono-spaced alignment
    const itemsHtml = order.items?.map((item) => {
      const customization = item.removed_ingredients && item.removed_ingredients.length > 0
        ? `<div class="item-custom">(${item.removed_ingredients.map((i: string) => `Sin ${i}`).join(", ")})</div>`
        : "";
      const itemTotal = (item.quantity * item.unit_price).toFixed(2);
      return `
        <div class="item-row">
          <span class="item-desc">${escapeHtml(item.product_name)}</span>
          <span class="item-qty">${item.quantity}</span>
          <span class="item-price">${itemTotal}€</span>
        </div>
        ${customization}
      `;
    }).join("") || '<div class="item-row"><span>Sin artículos</span></div>';

    // Order type text
    const getOrderTypeText = () => {
      if (order.order_type === "mesa") return `Mesa Nº ${order.table_number}`;
      if (order.order_type === "llevar") return "Para Llevar";
      return "Encargo";
    };

    // Payment method text
    const getPaymentMethodText = () => {
      if (order.payment_method === "online") return "TARJETA";
      if (order.cash_received) return "EFECTIVO";
      return "EFECTIVO";
    };

    // Pickup info for encargos
    const pickupInfo = order.order_type === "encargo" && order.pickup_date
      ? `
        <div class="separator-dashed"></div>
        <div class="pickup-block">
          <div class="pickup-title">📅 RECOGIDA</div>
          <div class="pickup-date">${escapeHtml(order.pickup_date)} ${escapeHtml(order.pickup_time || "")}</div>
          ${order.pickup_location ? `<div class="pickup-location">📍 ${escapeHtml(order.pickup_location)}</div>` : ""}
        </div>
      `
      : "";

    // Cash breakdown (only for cash payments)
    const cashBreakdownHtml = order.cash_received && order.change_returned !== undefined
      ? `
        <div class="separator-dashed"></div>
        <div class="cash-breakdown">
          <div class="cash-line"><span>ENTREGADO:</span><span>${order.cash_received.toFixed(2)}€</span></div>
          <div class="cash-line change"><span>CAMBIO:</span><span>${order.change_returned.toFixed(2)}€</span></div>
        </div>
      `
      : "";

    // Card receipt footer (only for online/card payments)
    const cardReceiptHtml = order.payment_method === "online" && order.stripe_session_id
      ? `
        <div class="separator-double"></div>
        <div class="card-receipt">
          <div class="card-title">CUSTOMER RECEIPT</div>
          <div class="card-approved">Card payment APPROVED</div>
          <div class="separator-dashed"></div>
          <div class="card-details">
            <div class="card-row"><span>Date/Time:</span><span>${formatDateTime(order.created_at)}</span></div>
            <div class="card-row"><span>Merchant ID:</span><span>${COMPANY_FISCAL_DATA.cif}</span></div>
            <div class="card-row"><span>Terminal ID:</span><span>${locationFiscal.tpvId}</span></div>
            <div class="card-row"><span>Auth Code:</span><span>${order.stripe_session_id.slice(-8).toUpperCase()}</span></div>
            <div class="card-row"><span>Card:</span><span>**** **** **** 4242</span></div>
          </div>
          <div class="card-retain">Please retain for your records</div>
        </div>
      `
      : "";

    const html = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <title>Factura Simplificada ${invoiceNumber}</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          @media print { 
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            * { box-shadow: none !important; border-radius: 0 !important; text-shadow: none !important; }
          }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Courier New', 'Lucida Console', monospace; 
            font-size: 11px; 
            width: 80mm; 
            padding: 2mm; 
            line-height: 1.3; 
            color: #000; 
            background: #fff;
          }
          
          /* Separators */
          .separator-double { border-top: 2px double #000; margin: 6px 0; }
          .separator-dashed { border-top: 1px dashed #000; margin: 6px 0; }
          .separator-solid { border-top: 1px solid #000; margin: 6px 0; }
          .separator-stars { text-align: center; padding: 6px 0; font-size: 10px; letter-spacing: 1px; }
          
          /* Header */
          .header { text-align: center; padding-bottom: 6px; }
          .logo { font-size: 16px; font-weight: bold; letter-spacing: 1px; margin-bottom: 2px; }
          .company-name { font-size: 9px; font-weight: bold; margin-bottom: 2px; }
          .cif { font-size: 10px; font-weight: bold; }
          
          /* Store info */
          .store-info { text-align: center; font-size: 9px; padding: 4px 0; line-height: 1.4; }
          .store-name { font-weight: bold; font-size: 10px; }
          
          /* Ticket data */
          .ticket-data { padding: 6px 0; font-size: 10px; }
          .ticket-row { display: flex; justify-content: space-between; margin: 2px 0; }
          
          /* Order info */
          .order-info { padding: 4px 0; }
          .order-type { font-weight: bold; font-size: 12px; text-align: center; margin-bottom: 4px; }
          .customer-info { font-size: 10px; }
          
          /* Items */
          .items-header { 
            display: flex; 
            justify-content: space-between; 
            font-weight: bold; 
            font-size: 9px; 
            padding: 4px 0; 
            border-bottom: 1px solid #000;
          }
          .items-header span:first-child { flex: 1; }
          .items-header span:nth-child(2) { width: 30px; text-align: center; }
          .items-header span:last-child { width: 50px; text-align: right; }
          
          .item-row { 
            display: flex; 
            justify-content: space-between; 
            padding: 3px 0; 
            font-size: 10px;
          }
          .item-desc { flex: 1; overflow: hidden; }
          .item-qty { width: 30px; text-align: center; }
          .item-price { width: 50px; text-align: right; }
          .item-custom { 
            font-size: 9px; 
            color: #e65100; 
            font-weight: bold; 
            margin-left: 8px; 
            margin-bottom: 2px;
          }
          
          /* Totals */
          .totals { padding: 6px 0; }
          .total-row { display: flex; justify-content: space-between; padding: 2px 0; font-size: 10px; }
          .total-row.base { font-size: 10px; }
          .total-row.igic { font-size: 10px; }
          .total-row.grand { 
            font-size: 16px; 
            font-weight: bold; 
            padding: 8px 0; 
            border-top: 2px double #000;
            border-bottom: 2px double #000;
            margin: 4px 0;
          }
          
          /* Payment method */
          .payment-method { 
            text-align: center; 
            padding: 6px 0; 
            font-size: 11px;
          }
          .payment-method .label { font-weight: normal; }
          .payment-method .value { font-weight: bold; }
          
          /* Cash breakdown */
          .cash-breakdown { padding: 4px 0; }
          .cash-line { display: flex; justify-content: space-between; font-size: 11px; padding: 2px 0; }
          .cash-line.change { font-weight: bold; color: #2e7d32; }
          
          /* Pickup info */
          .pickup-block { text-align: center; padding: 6px 0; }
          .pickup-title { font-weight: bold; font-size: 10px; }
          .pickup-date { font-size: 12px; font-weight: bold; margin: 2px 0; }
          .pickup-location { font-size: 10px; }
          
          /* Observations */
          .observations { padding: 6px; background: #f5f5f5; font-size: 9px; margin: 6px 0; }
          
          /* Loyalty block */
          .loyalty-block { 
            text-align: center; 
            padding: 8px 4px; 
            margin: 6px 0; 
            border: 1px dashed #000; 
          }
          .loyalty-title { font-weight: bold; font-size: 11px; margin-bottom: 4px; }
          .loyalty-member { font-size: 10px; margin: 2px 0; }
          .loyalty-points { font-size: 10px; margin: 2px 0; }
          .loyalty-points strong { font-size: 11px; }
          .loyalty-qr { 
            margin: 6px auto; 
            width: 50px; 
            height: 50px; 
            border: 1px dashed #666; 
            display: flex; 
            align-items: center; 
            justify-content: center; 
            font-size: 7px; 
            color: #666;
          }
          .loyalty-url { font-size: 8px; color: #666; }
          
          /* Card receipt */
          .card-receipt { text-align: center; padding: 6px 0; }
          .card-title { font-weight: bold; font-size: 10px; margin-bottom: 2px; }
          .card-approved { font-size: 10px; margin-bottom: 4px; }
          .card-details { text-align: left; padding: 4px 8px; font-size: 9px; }
          .card-row { display: flex; justify-content: space-between; padding: 1px 0; }
          .card-retain { font-size: 8px; margin-top: 4px; font-style: italic; }
          
          /* Legal footer */
          .legal-footer { text-align: center; padding: 6px 0; font-size: 8px; line-height: 1.4; }
          .verifactu { padding: 4px; border: 1px solid #666; font-size: 7px; margin: 4px 0; }
          .qr-verification { 
            margin: 6px auto; 
            width: 40px; 
            height: 40px; 
            border: 1px dashed #999; 
            display: flex; 
            align-items: center; 
            justify-content: center; 
            font-size: 6px; 
            color: #666;
          }
          
          /* Thanks */
          .thanks { 
            text-align: center; 
            margin-top: 8px; 
            padding-top: 6px;
            font-size: 11px; 
            font-weight: bold; 
          }
          .since { font-size: 9px; font-weight: normal; font-style: italic; }
        </style>
      </head>
      <body>
        <!-- HEADER -->
        <div class="header">
          <div class="logo">🥐 PASTELERÍA REYES</div>
          <div class="company-name">${COMPANY_FISCAL_DATA.razonSocial}</div>
          <div class="cif">CIF: ${COMPANY_FISCAL_DATA.cif}</div>
        </div>
        
        <div class="separator-double"></div>
        
        <!-- STORE INFO -->
        <div class="store-info">
          <div class="store-name">${escapeHtml(locationFiscal.name)}</div>
          <div>${escapeHtml(locationFiscal.fullAddress || locationFiscal.address)}</div>
          <div>Tel: ${escapeHtml(locationFiscal.phone)}</div>
        </div>
        
        <div class="separator-double"></div>
        
        <!-- TICKET DATA -->
        <div class="ticket-data">
          <div class="ticket-row">
            <span>N.TPV: ${locationFiscal.tpvId}</span>
            <span>Fecha: ${formatDate(order.created_at)}</span>
          </div>
          <div class="ticket-row">
            <span>FACT.SIMP: ${invoiceNumber}</span>
            <span>Hora: ${formatTime(order.created_at)}</span>
          </div>
          ${order.served_by ? `<div class="ticket-row"><span>Atendido por:</span><span style="font-weight:bold">${escapeHtml(order.served_by)}</span></div>` : order.payment_method === "online" ? `<div class="ticket-row"><span>Atendido por:</span><span style="font-weight:bold">Web</span></div>` : ""}
        </div>
        
        <div class="separator-double"></div>
        
        <!-- ORDER INFO -->
        <div class="order-info">
          <div class="order-type">${getOrderTypeText()}</div>
          ${order.customer_name ? `<div class="customer-info">Cliente: ${escapeHtml(order.customer_name)}</div>` : ""}
          ${order.customer_phone ? `<div class="customer-info">Tel: ${escapeHtml(order.customer_phone)}</div>` : ""}
        </div>
        
        <div class="separator-dashed"></div>
        
        <!-- ITEMS -->
        <div class="items-header">
          <span>DESCRIPCIÓN</span>
          <span>CANT.</span>
          <span>IMPORTE</span>
        </div>
        <div class="items-list">
          ${itemsHtml}
        </div>
        
        <div class="separator-dashed"></div>
        
        <!-- TOTALS -->
        <div class="totals">
          <div class="total-row base">
            <span>Base Imponible:</span>
            <span>${baseImponible.toFixed(2)}€</span>
          </div>
          <div class="total-row igic">
            <span>IGIC (${(IGIC_RATE * 100).toFixed(0)}%):</span>
            <span>${cuotaIGIC.toFixed(2)}€</span>
          </div>
          <div class="total-row grand">
            <span>TOTAL A PAGAR:</span>
            <span>${totalWithTax.toFixed(2)}€</span>
          </div>
        </div>
        
        <!-- PAYMENT METHOD -->
        <div class="payment-method">
          <span class="label">Forma de Pago:</span>
          <span class="value">${getPaymentMethodText()}</span>
        </div>
        
        ${cashBreakdownHtml}
        
        ${pickupInfo}
        
        ${order.observations ? `<div class="observations"><strong>Obs:</strong> ${escapeHtml(order.observations)}</div>` : ""}
        
        <!-- LOYALTY BLOCK -->
        <div class="separator-stars">★★★★★★★★★★★★★★★★★★★★</div>
        <div class="loyalty-block">
          <div class="loyalty-title">✨ CLUB REYES ✨</div>
          <div class="loyalty-member">Socio: ${loyaltyMemberNumber}</div>
          <div class="loyalty-points">Puntos esta compra: <strong>+${pointsEarned} pts</strong></div>
          <div class="loyalty-points">Saldo total: <strong>${totalPoints} pts</strong></div>
          <div class="loyalty-qr">[QR CLUB]</div>
          <div class="loyalty-url">www.pasteleriareyes.com/club</div>
        </div>
        <div class="separator-stars">★★★★★★★★★★★★★★★★★★★★</div>
        
        ${cardReceiptHtml}
        
        <!-- LEGAL FOOTER -->
        <div class="separator-dashed"></div>
        <div class="legal-footer">
          <p>Conserve este documento como justificante</p>
          <div class="verifactu">${VERIFACTU_TEXT}</div>
          <div class="qr-verification">[QR AEAT]</div>
        </div>
        
        <!-- THANKS -->
        <div class="thanks">
          ¡GRACIAS POR SU VISITA!
          <div class="since">Desde 1979</div>
        </div>
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Special print for encargos - larger format with prominent customer name and pickup date
  const handlePrintEncargo = () => {
    const printWindow = window.open("", "_blank", "width=600,height=800");
    if (!printWindow) return;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Hoja de Encargo #${order.order_number}</title>
        <style>
          @page { size: A5 landscape; margin: 10mm; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          body { font-family: Arial, sans-serif; padding: 20px; margin: 0; }
          .header { text-align: center; border-bottom: 3px solid #5D4037; padding-bottom: 15px; margin-bottom: 20px; }
          .header h1 { color: #5D4037; margin: 0; font-size: 28px; }
          .header p { margin: 5px 0 0; color: #666; }
          .highlight-box { 
            background: #fff3cd; 
            border: 4px solid #ffc107; 
            border-radius: 12px; 
            padding: 25px; 
            margin: 20px 0; 
            text-align: center;
          }
          .client-name { 
            font-size: 42px; 
            font-weight: bold; 
            color: #5D4037; 
            margin: 0;
            text-transform: uppercase;
          }
          .pickup-date { 
            font-size: 32px; 
            font-weight: bold; 
            color: #d32f2f; 
            margin: 15px 0 0;
          }
          .pickup-time {
            font-size: 24px;
            color: #666;
            margin-top: 5px;
          }
          .pickup-location {
            font-size: 18px;
            color: #555;
            margin-top: 10px;
          }
          .order-details { margin-top: 25px; }
          .order-details h3 { color: #5D4037; margin-bottom: 10px; font-size: 18px; }
          .items-list { 
            background: #f5f5f5; 
            padding: 15px; 
            border-radius: 8px; 
          }
          .item { padding: 8px 0; border-bottom: 1px dashed #ccc; font-size: 16px; }
          .item:last-child { border-bottom: none; }
          .observations { 
            background: #e3f2fd; 
            padding: 15px; 
            border-radius: 8px; 
            margin-top: 15px;
            font-size: 16px;
          }
          .observations strong { color: #1565c0; }
          .footer { 
            text-align: center; 
            margin-top: 20px; 
            font-size: 14px; 
            color: #888;
            border-top: 1px solid #ddd;
            padding-top: 15px;
          }
          .total-box {
            background: #e8f5e9;
            padding: 15px;
            border-radius: 8px;
            text-align: center;
            margin-top: 15px;
            font-size: 20px;
            font-weight: bold;
            color: #2e7d32;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🎂 HOJA DE ENCARGO</h1>
          <p>Pedido #${order.order_number}</p>
        </div>
        
        <div class="highlight-box">
          <p class="client-name">${escapeHtml(order.customer_name || "Sin nombre")}</p>
          <p class="pickup-date">📅 ${escapeHtml(order.pickup_date || "")}</p>
          <p class="pickup-time">🕐 ${escapeHtml(order.pickup_time || "")}</p>
          ${order.pickup_location ? `<p class="pickup-location">📍 ${escapeHtml(order.pickup_location)}</p>` : ""}
        </div>
        
        <div class="order-details">
          <h3>📦 Productos del Encargo:</h3>
          <div class="items-list">
            ${order.items?.map(item => {
              const customization = item.removed_ingredients && item.removed_ingredients.length > 0
                ? `<div style="color:#e65100;font-size:14px;font-weight:bold;margin-left:20px;">(${item.removed_ingredients.map((i: string) => `Sin ${i}`).join(", ")})</div>`
                : "";
              return `<div class="item"><strong>${item.quantity}x</strong> ${escapeHtml(item.product_name)}${customization}</div>`;
            }).join("") || "Sin items"}
          </div>
        </div>
        
        ${order.observations ? `
          <div class="observations">
            <strong>📝 Observaciones:</strong><br>
            ${escapeHtml(order.observations)}
          </div>
        ` : ""}

        <div class="total-box">
          💰 Total: ${order.total.toFixed(2).replace(".", ",")}€
        </div>
        
        <div class="footer">
          <p><strong>Pastelería Reyes</strong> - Tradición desde 1979</p>
          ${order.customer_phone ? `<p>📞 ${escapeHtml(order.customer_phone)}</p>` : ""}
        </div>
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <Card className={`border-2 ${getCardColor()} transition-all`} data-order-id={order.id}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold text-primary">
              {order.ticket_series && order.series_number
                ? `#${order.ticket_series}-${order.series_number}`
                : `#${order.order_number}`}
            </span>
            <Badge variant="outline" className={getStatusColor(order.status)}>
              {getStatusEmoji()} {getStatusLabel()}
            </Badge>
          </div>
          <div className="text-right text-sm text-muted-foreground">
            <Clock className="h-3 w-3 inline mr-1" />
            {formatTime(order.created_at)}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          <Badge variant="outline" className={`gap-1 ${getOrderTypeBadgeStyle()}`}>
            {getOrderTypeIcon()}
            {getOrderTypeLabel()}
          </Badge>
          <Badge
            variant="outline"
            className={
              order.payment_status === "pagado"
                ? "bg-green-50 text-green-700 border-green-300"
                : "bg-amber-50 text-amber-700 border-amber-300"
            }
          >
            {order.payment_method === "online" ? (
              <CreditCard className="h-3 w-3 mr-1" />
            ) : (
              <Banknote className="h-3 w-3 mr-1" />
            )}
            {order.payment_status === "pagado" ? "Pagado" : "Pendiente"}
          </Badge>
          {showLocation && order.locations?.name && (
            <Badge variant="outline" className="gap-1 bg-blue-50 text-blue-700 border-blue-300">
              <Building2 className="h-3 w-3" />
              {order.locations.name}
            </Badge>
          )}
          {/* Warehouse ready indicator */}
          {order.order_type === "encargo" && order.warehouse_status === "ready" && (
            <Badge className="gap-1 bg-purple-500 text-white border-purple-600 animate-pulse">
              <PackageCheck className="h-3 w-3" />
              📦 LISTO EN TIENDA
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {(order.customer_name || order.customer_phone || order.customer_email) && (
          <div className="text-sm space-y-1">
            {order.customer_name && (
              <p className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" /> {order.customer_name}
              </p>
            )}
            {order.customer_phone && (
              <p className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" /> {order.customer_phone}
              </p>
            )}
            {order.customer_email && (
              <p className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" /> {order.customer_email}
              </p>
            )}
          </div>
        )}

        {order.order_type === "encargo" && (order.pickup_date || order.pickup_location) && (
          <div className="text-sm bg-muted/50 p-3 rounded-lg space-y-1">
            {order.pickup_date && (
              <p className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-secondary" />
                <span className="font-medium">
                  {order.pickup_date} a las {order.pickup_time}
                </span>
              </p>
            )}
            {order.pickup_location && (
              <p className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-secondary" />
                {order.pickup_location}
              </p>
            )}
          </div>
        )}

        {order.observations && (
          <div className="text-sm bg-accent/30 p-3 rounded-lg">
            <p className="font-medium text-primary mb-1">Observaciones:</p>
            <p className="text-muted-foreground">{order.observations}</p>
          </div>
        )}

        {/* Order items summary */}
        {order.items && order.items.length > 0 && (
          <div className="text-sm bg-muted/30 p-2 rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Productos:</p>
            <p className="font-medium">
              {order.items.slice(0, 4).map((item, i) => (
                <span key={item.id}>
                  {item.quantity}x {item.product_name}
                  {i < Math.min(order.items!.length, 4) - 1 ? ", " : ""}
                </span>
              ))}
              {order.items.length > 4 && <span className="text-muted-foreground"> +{order.items.length - 4} más</span>}
            </p>
          </div>
        )}

        {/* Atendido por badge */}
        {order.served_by && (
          <div className="text-sm flex items-center gap-2 text-muted-foreground">
            <User className="h-4 w-4" />
            <span>Atendido por: <strong className="text-foreground">{order.served_by}</strong></span>
          </div>
        )}
        {order.payment_method === "online" && !order.served_by && (
          <div className="text-sm flex items-center gap-2 text-muted-foreground">
            <CreditCard className="h-4 w-4" />
            <span>Atendido por: <strong className="text-foreground">Web</strong></span>
          </div>
        )}

        {order.total > 0 && (
          <div className="flex justify-between items-center pt-2 border-t border-border">
            <span className="font-medium text-primary">Total</span>
            <span className="text-xl font-bold text-secondary">{order.total.toFixed(2).replace(".", ",")}€</span>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {/* PRINT BUTTONS: Diferenciados según estado de pago */}
          {/* Si NO está pagado: "Imprimir Cuenta" (amarillo) */}
          {order.payment_status !== "pagado" && (
            <Button 
              variant="outline" 
              size="lg" 
              className="flex-1 h-12 min-w-[140px] bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-300"
              onClick={() => onPrintCuenta ? onPrintCuenta(order) : handlePrint()}
            >
              <Printer className="h-4 w-4 mr-2" /> 📝 Imprimir Cuenta
            </Button>
          )}
          
          {/* Si está PAGADO: "Imprimir Ticket" (verde) */}
          {order.payment_status === "pagado" && (
            <Button 
              variant="outline" 
              size="lg" 
              className="flex-1 h-12 min-w-[140px] bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
              onClick={() => onPrintTicket ? onPrintTicket(order) : handlePrint()}
            >
              <Printer className="h-4 w-4 mr-2" /> 🧾 Imprimir Ticket
            </Button>
          )}

          {order.order_type === "encargo" && (
            <Button 
              variant="outline" 
              size="lg" 
              className="flex-1 h-12 min-w-[140px] bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-300"
              onClick={handlePrintEncargo}
            >
              <Package className="h-4 w-4 mr-2" /> Hoja Encargo
            </Button>
          )}

          {/* ENCARGO FLOW: Pendiente -> "Enviar a Almacén" (purple) */}
          {isEncargoMode && order.order_type === "encargo" && order.status === "pendiente" && (
            <Button
              size="lg"
              className="flex-1 h-12 min-w-[140px] bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => onStatusChange(order.id, "en_almacen")}
            >
              📦 Enviar a Almacén
            </Button>
          )}

          {/* ENCARGO FLOW: En Almacén - Just shows status, no action for store (warehouse handles it) */}
          {isEncargoMode && order.order_type === "encargo" && order.status === "en_almacen" && (
            <div className="flex-1 h-12 min-w-[140px] flex items-center justify-center text-purple-600 bg-purple-50 rounded-md border border-purple-200">
              📦 Esperando elaboración en Almacén
            </div>
          )}

          {/* NON-ENCARGO FLOW: Pendiente -> "Marcar como Listo" (green) */}
          {!isEncargoMode && order.status === "pendiente" && (
            <Button
              size="lg"
              className="flex-1 h-12 min-w-[140px] bg-green-600 hover:bg-green-700 text-white"
              onClick={() => onStatusChange(order.id, "completado")}
            >
              ✅ Marcar como Listo
            </Button>
          )}

          {/* ENCARGO READY FOR PICKUP: Big "COBRAR Y ENTREGAR" button */}
          {order.order_type === "encargo" &&
           order.status === "completado" && 
           order.payment_status !== "pagado" &&
           onMarkAsPaid && (
            <Button
              size="lg"
              className="w-full h-16 text-lg font-bold bg-green-600 hover:bg-green-700 text-white shadow-lg animate-pulse"
              onClick={() => onMarkAsPaid(order.id)}
            >
              💸 COBRAR Y ENTREGAR ({order.total.toFixed(2)}€)
            </Button>
          )}

          {/* REGULAR (non-encargo): Completado + NO pagado -> "Cobrar" (yellow) */}
          {order.order_type !== "encargo" &&
           order.status === "completado" && 
           order.payment_status !== "pagado" &&
           onMarkAsPaid && (
            <Button
              size="lg"
              className="flex-1 h-12 min-w-[140px] bg-amber-500 hover:bg-amber-600 text-white"
              onClick={() => onMarkAsPaid(order.id)}
            >
              💸 Cobrar ({order.total.toFixed(2)}€)
            </Button>
          )}

          {/* LOGIC: Completado + SOLO pagado -> "Pedido Finalizado" (gray text) */}
          {order.status === "completado" && 
           order.payment_status === "pagado" && (
            <div className="flex-1 h-12 min-w-[140px] flex items-center justify-center text-gray-500 bg-gray-100 rounded-md border border-gray-200">
              ✓ Pedido Finalizado
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default OrderCard;
